import { MultiSelect, Select, createStyles, rem } from "@mantine/core";
import { useEffect, useState } from "react";
import { FiChevronLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import styled from "styled-components";
import { Unit } from "../../entities/unit";
import { BiService } from "../../services/bi/bi.service";
import { Lesson, LessonService } from "../../services/lesson/lesson.service";
import { UnitService } from "../../services/unit/unit.service";
import { Chart, ChartPanel, FiltersPanel, PageContent, PageHeader, PageWrapper } from "./styles";

const useStyles = createStyles((theme) => ({
  root: {
    position: "relative",
    marginBottom: rem(2.4),
  },
  input: {
    height: rem(56),
    paddingTop: rem(18),
  },
  label: {
    position: "absolute",
    pointerEvents: "none",
    fontSize: theme.fontSizes.xs,
    paddingLeft: theme.spacing.sm,
    paddingTop: `calc(${theme.spacing.sm} / 2)`,
    zIndex: 1,
  },
}));

export function MembrosFaltosos() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [unitites, setUnities] = useState<Unit[]>(new Array<Unit>());
  const [lessons, setLessons] = useState<Lesson[]>(new Array<Lesson>());
  const [unitiesSelected, setUnitiesSelected] = useState<string[]>([]);
  const [lessonsSelected, setLessonsSelected] = useState<string | null>(null);
  const [unidades, setUnidades] = useState<string[]>([]);
  const [presentes, setPresentes] = useState<number[]>([]);
  const [faltantes, setFaltantes] = useState<number[]>([]);
  const [total, setTotal] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const option = {
    title: { text: "Relação de presenças e faltas", left: "center" },
    legend: { bottom: "bottom" },
    toolbox: { feature: { saveAsImage: { title: "Salvar imagem" } } },
    tooltip: {
      fontSize: 36,
      formatter: (data: any) =>
        `<b>${data?.name}</b> </br> Quantidade: <b>${data?.value}</b>`,
    },
    xAxis: [{ type: "category", data: unidades }],
    yAxis: [{ type: "value" }],
    series: [
      {
        data: presentes,
        name: "Presentes",
        type: "bar",
        showBackground: true,
        stack: "total",
        emphasis: { focus: "series" },
        color: "blue",
        label: { show: true, fontSize: 14 },
      },
      {
        data: faltantes,
        name: "Faltantes",
        type: "bar",
        stack: "total",
        emphasis: { focus: "series" },
        showBackground: true,
        color: "orange",
        label: { show: true, fontSize: 14 },
      },
    ],
  };

  const fetch = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const [response, responseLesson] = await Promise.all([
        UnitService.get(),
        LessonService.getAtDate(),
      ]);
      setUnities(response);
      setLessons(responseLesson);
    } catch (error: any) {
      toast.warning(error[0], { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const unit_ids: string[] = [];
      unitiesSelected.forEach((uni) => {
        const unitId = unitites.find((u) => u.name === uni)?.id;
        if (unitId) unit_ids.push(unitId);
      });

      if (lessonsSelected) {
        const [anoS, trimestreS, licaoS] = lessonsSelected.split(";");
        const year = Number(anoS.replace(/[^0-9]/g, ""));
        const quarter = Number(trimestreS.replace(/[^0-9]/g, ""));
        const number = Number(licaoS.replace(/[^0-9]/g, ""));

        const response = await BiService.getPresentMissingRelation({ number, quarter, year, unit_ids });

        if (!response?.items?.length) {
          toast.warning("Não há dados para serem exibidos", { position: toast.POSITION.TOP_RIGHT });
          return;
        }

        const u: any[] = [], p: any[] = [], f: any[] = [], t: any[] = [];
        response.items.forEach((r) => {
          u.push(r.label);
          p.push(r.result.find((i) => i.key === "Presentes")?.value);
          f.push(r.result.find((i) => i.key === "Faltantes")?.value);
          t.push(r.result.find((i) => i.key === "Total")?.value);
        });

        setUnidades(u);
        setPresentes(p);
        setFaltantes(f);
        setTotal(t);
      } else {
        setUnidades([]); setPresentes([]); setFaltantes([]); setTotal([]);
      }
    } catch (error: any) {
      toast.warning(error[0], { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  return (
    <PageWrapper>
      <PageHeader onClick={() => navigate(-1)}>
        <FiChevronLeft />
        <span>Relação de presenças e faltas</span>
      </PageHeader>

      <PageContent>
        <FiltersPanel>
          <MultiSelect
            mt="md"
            withinPortal
            data={unitites.map((u) => u?.name) as any}
            placeholder="Todas as unidades"
            label="Unidade(s)"
            classNames={classes}
            onChange={setUnitiesSelected}
          />
          <Select
            mt="md"
            withinPortal
            data={lessons.map((l) => `Ano: ${l.year}; Trimestre: ${l.quarter}; Lição: ${l.number}`)}
            placeholder="Selecione uma lição"
            label="Lição"
            classNames={classes}
            onChange={setLessonsSelected}
          />
          <SearchButton onClick={fetchData} disabled={isLoading}>
            {isLoading ? <Spinner color="light" style={{ width: 18, height: 18 }} /> : <label style={{ cursor: "pointer" }}>Buscar</label>}
          </SearchButton>
        </FiltersPanel>

        <ChartPanel>
          <Chart option={option} />
        </ChartPanel>
      </PageContent>
    </PageWrapper>
  );
}

const SearchButton = styled.button`
  height: 3.5rem;
  border-radius: 0.4rem;
  background-color: #3439a5;
  border: 0.1rem solid #2b2fa3;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.8rem;

  label {
    color: #fff;
    font-size: 1.4rem;
    font-weight: bold;
  }

  &:disabled { opacity: 0.6; }
`;
